import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainContentLayout from "../../components/mainContentLayout";
import PageHeader from "../../components/pageHeader";
import { API_BASE } from "../../config/apiConfig";
import { useSelector, useDispatch } from "react-redux";
import { addLog } from "../../features/ptLogs/ptLogsSlice";
import LoadingOverlay from "../../components/loadingOverlay";
import { useToast } from "../../hooks/useToast";
import { FaCheck, FaTimes } from "react-icons/fa";

export default function AddNewPTLog() {
  const token = sessionStorage.getItem("access_token");
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { showToast, ToastContainer } = useToast();

  const [date, setDate] = useState(() => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  });

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------
  // Load PT guidelines
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadGuidelines() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/pt-player-guidelines`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();

        if (data.success && Array.isArray(data.guidelines) && data.guidelines.length > 0) {
          setQuestions(data.guidelines);
          setAnswers(data.guidelines.map(() => ""));
        } else {
          setQuestions(["What did you train today?"]);
          setAnswers([""]);
        }
      } catch (err) {
        showToast("Failed to load guidelines.", "error");
        setQuestions(["What did you train today?"]);
        setAnswers([""]);
      }
      setLoading(false);
    }

    loadGuidelines();
  }, []);

  // ------------------------------------------------------------
  // Update answer in state
  // ------------------------------------------------------------
  function handleAnswerChange(i, value) {
    setAnswers((prev) => prev.map((a, idx) => (idx === i ? value : a)));
  }

  const allFilled = answers.every((a) => a.trim() !== "");

  // ------------------------------------------------------------
  // Submit new PT log
  // ------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    if (!allFilled) return;

    const payload = {
      date: new Date(date).toISOString(),
      answers: questions.map((q, i) => ({
        question: q,
        answer: answers[i],
      })),
    };

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/pt-log-add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.success) {
        showToast(data.message || "Failed to add record.", "error");
        setLoading(false);
        return;
      }

      const newLog = {
        id: data.log.id,
        date: data.log.date,
      };

      // Build Redux cache key
      const d = new Date(date);
      const key = `player-${user.user_id}-${d.getFullYear()}-${d.getMonth() + 1}`;

      dispatch(addLog({ key, log: newLog }));

      navigate("/pt-logs-list");

    } catch (err) {
      console.error("Add PT log error:", err);
      showToast("Network error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <MainContentLayout>
      <PageHeader title="Add New PT Log" backTo="/pt-logs-list" />
      <LoadingOverlay show={loading} />
      <ToastContainer />

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Buttons */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            type="submit"
            disabled={!allFilled}
            className={`p-1 rounded text-white ${
              allFilled
                ? "bg-blue-600 hover:bg-blue-800"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            title="Submit"
          >
            <FaCheck size={22} />
          </button>

          <button
            type="button"
            onClick={() => navigate("/pt-logs-list")}
            className="p-1 bg-gray-300 rounded"
            title="Cancel"
          >
            <FaTimes size={22} />
          </button>
        </div>
        
        {/* Date Input */}
        <div>
          <label className="block mb-1">Date & Time</label>
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border p-2 w-full"
          />
        </div>

        {/* Questions */}
        <div>
          <h3 className="mb-2">Training Questions</h3>
          {questions.map((q, i) => (
            <div key={i} className="mb-3 text-left">
              <label className="block text-sm font-bold mb-1">
                {i + 1}. {q} <span className="text-red-500">*</span>
              </label>
              <textarea
                rows="3"
                value={answers[i]}
                onChange={(e) => handleAnswerChange(i, e.target.value)}
                className="border p-2 w-full placeholder:italic placeholder:text-gray-400"
                placeholder="Write your answer here..."
              />
            </div>
          ))}
        </div>
      </form>
    </MainContentLayout>
  );
}
